import React, { useState, useRef } from 'react';

import { ApCheckbox } from '@alixpartners/ui-components';
import {
	Wrapper,
	HeaderRow,
	SortArrow,
	SortIconSpace,
	HeaderCellWrapper,
	SpanWrapper,
	RowSpan,
	SpanColumn,
	HeaderCell,
	HeaderText,
	HeaderCellNumber,
	Text,
	HeaderScrollWrapper,
	Content,
	CheckboxWrapper,
} from './PTItemsTable.styles';
import { VirtualList } from './VirtualList';
import orderBy from 'lodash/orderBy';
import { ITEMS_TYPE } from '../../utils';

const SORT_DIRECTION = {
	ASC: 'asc',
	DESC: 'desc',
};

export const PTItemsTable = ({
	data,
	items,
	companies,
	spendFormatter,
	onItemsChange,
	highlightId,
	wacc,
	itemsType,
	showTopVendors,
}) => {
	const [sort, setSort] = useState({});
	const wrapperRef = useRef();
	const headerRef = useRef();

	const renderSortArrow = column => {
		if (!sort || sort.column !== column) {
			return <SortIconSpace />;
		}

		return (
			<SortArrow
				iconName="baseline_arrow_forward"
				iconSize={13}
				reverse={sort.direction === SORT_DIRECTION.DESC}
			/>
		);
	};

	const onSortChange = (column, isCompany = false) => {
		let newSort = { ...sort };

		if (sort.column === column) {
			if (sort.direction === SORT_DIRECTION.ASC) {
				newSort.direction = SORT_DIRECTION.DESC;
			} else {
				newSort = {};
			}
		} else {
			newSort = {
				column,
				direction: SORT_DIRECTION.ASC,
				isCompany,
			};
		}

		setSort(newSort);
	};

	const getSortedData = () => {
		const func = item => {
			return sort.isCompany
				? item.companies[sort.column] || 0
				: item[sort.column];
		};

		return orderBy(data, func, sort.direction);
	};

	const handleInputChange = update => {
		onItemsChange(
			items.map(item => {
				if (item.id === update.id) {
					return { ...item, ...update };
				}

				return item;
			})
		);
	};

	const handleItemChange = itemData => {
		let updatedItems = [];
		const currentIndex = items.findIndex(
			item =>
				item.name === itemData.name && item.groupName === itemData.groupName
		);

		if (currentIndex === -1) {
			updatedItems = [...items, itemData];
		} else {
			updatedItems = [
				...items.slice(0, currentIndex),
				...items.slice(currentIndex + 1),
			];
		}

		onItemsChange(updatedItems);
	};

	const notUsedData = data.filter(item => !item.isUsed);
	const isAllChecked =
		items.length > 0 &&
		notUsedData.every(item =>
			items.some(
				node => item.name === node.name && item.groupName === node.groupName
			)
		);

	const handleAllItemsChange = () => {
		const usedItems = items.filter(item => item.isUsed);

		onItemsChange(isAllChecked ? usedItems : [...usedItems, ...notUsedData]);
	};

	const isPartiallyChecked =
		items.length > 0 &&
		notUsedData.some(
			item =>
				!items.some(
					node => item.name === node.name && item.groupName === node.groupName
				)
		);

	return (
		<Wrapper>
			<HeaderScrollWrapper innerRef={headerRef}>
				<HeaderRow>
					<HeaderCell>
						<CheckboxWrapper>
							<ApCheckbox
								onClick={handleAllItemsChange}
								onChange={() => {}}
								checked={isAllChecked}
								indeterminate={isPartiallyChecked}
							/>
						</CheckboxWrapper>
						<HeaderCellWrapper onClick={() => onSortChange('name')}>
							{itemsType === ITEMS_TYPE.PT_IMPROVEMENT
								? 'L1 category'
								: 'Vendor name'}
							{renderSortArrow('name')}
						</HeaderCellWrapper>
					</HeaderCell>
					<HeaderCellNumber onClick={() => onSortChange('spend')}>
						Total spend{renderSortArrow('spend')}
					</HeaderCellNumber>
					{companies.map(companyName => (
						<HeaderCellNumber
							key={companyName}
							onClick={() => onSortChange(companyName, true)}
							title={companyName}
						>
							<Text>{companyName}</Text>
							{renderSortArrow(companyName)}
						</HeaderCellNumber>
					))}
					{itemsType === ITEMS_TYPE.PT_HARMONIZATION ? (
						<HeaderCellNumber onClick={() => onSortChange('targetDpo')}>
							<HeaderText>Harmonized DPO</HeaderText>
							{renderSortArrow('targetDpo')}
						</HeaderCellNumber>
					) : (
						<React.Fragment>
							<HeaderCellNumber
								onClick={() => onSortChange('weightedAverageDpo')}
							>
								Wt. Avg. DPO{renderSortArrow('weightedAverageDpo')}
							</HeaderCellNumber>
							<HeaderCellNumber>
								<HeaderText>Target DPO</HeaderText>
							</HeaderCellNumber>
						</React.Fragment>
					)}

					<SpanWrapper>
						<RowSpan>
							<HeaderText>Potential realizable</HeaderText>
						</RowSpan>
						<RowSpan>
							<SpanColumn>
								<HeaderText>Low%</HeaderText>
							</SpanColumn>
							<SpanColumn>
								<HeaderText>High%</HeaderText>
							</SpanColumn>
						</RowSpan>
					</SpanWrapper>
					<SpanWrapper>
						<RowSpan>
							<HeaderText>Working capital impact</HeaderText>
						</RowSpan>
						<RowSpan>
							<SpanColumn>
								<HeaderText>Low</HeaderText>
							</SpanColumn>
							<SpanColumn>
								<HeaderText>High</HeaderText>
							</SpanColumn>
						</RowSpan>
					</SpanWrapper>
				</HeaderRow>
			</HeaderScrollWrapper>
			<Content innerRef={wrapperRef}>
				<VirtualList
					wacc={wacc}
					wrapperRef={wrapperRef}
					headerRef={headerRef}
					data={getSortedData()}
					companies={companies}
					spendFormatter={spendFormatter}
					onInputChange={handleInputChange}
					onItemChange={handleItemChange}
					items={items}
					highlightId={highlightId}
					itemsType={itemsType}
					showTopVendors={showTopVendors}
				/>
			</Content>
		</Wrapper>
	);
};
